import { ExclamationCircleOutlined } from '@ant-design/icons'
import { useDriverSchedules } from '@api/useDriverSchedules'
import { dateFormat } from '@constants/dateFormat'
import { dispatchModes, dropActions, shifts } from '@constants/orderDispatch'
import { useQueryClient } from '@tanstack/react-query'
import { convertStorageStringToArray, getEventsFromScheduleData, titleCase } from '@utils/general'
import { Modal, notification } from 'antd'
import moment from 'moment'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { CellMeasurerCache } from 'react-virtualized'

import { optimisticallyRemoveOrder } from '@api/useDriverSchedules/util'
import { useUserSettings } from '@hooks/settings'
import _ from 'lodash'

import api from '@utils/api'

const { confirm } = Modal
type CtxType = {
  schedulePayload: {
    shift: string
    viewDate: string
  }
} & Record<string, unknown>
const ScheduleContext = createContext<undefined | CtxType>(undefined)
const store = window.localStorage

export function ScheduleContextProvider({ children }) {
  const [selectedDepots, setSelectedDepots] = useState(
    convertStorageStringToArray(store.getItem('order-dispatch-trip-depots'))
  )
  const [selectedOrders, setSelectedOrders] = useState([])

  const [loadingTripCard, setLoadingTripCard] = useState(null)

  const [selectedDrivers, setSelectedDrivers] = useState([])
  const [dispatchViewMode, setDispatchViewMode] = useState(dispatchModes.TRIPGRID)
  const [, setisApplyingChange] = useState(false)
  const [schedulePayload, setSchedulePayload] = useState({
    shift: store.getItem('trip-grid-shift') || shifts.AM,
    viewDate: store.getItem('trip-grid-view-date') || moment().utc().format(dateFormat.YEAR_MONTH_DATE),
  })
  const queryClient = useQueryClient()
  const [quickPayload, setQuickPayload] = useState()
  const {
    useDriverScheduleQuery,
    useQuickStatusQuery,
    assignOrderMutation,
    removeOrderMutation,
    moveOrderMutation,
    lockTripMutation,
    unlockTripMutation,
    unlinkOrderMutation,
    changeEquipmentMutation,
  } = useDriverSchedules()
  const {
    data: driverScheduleData,
    isFetching: loadingSchedule,
    refetch: refetchDriverSchedule,
  } = useDriverScheduleQuery(schedulePayload)

  const { updateSetting } = useUserSettings()
  const cellCache = useMemo(() => {
    return new CellMeasurerCache({
      defaultHeight: 100,
      minHeight: 75,
      fixedWidth: true,
    })
  }, [])

  const { data: quickStatusResponse } = useQuickStatusQuery(quickPayload)

  const calendarResources = useMemo(() => {
    if (driverScheduleData) return getResources(driverScheduleData)
  }, [driverScheduleData])

  const unfilteredResources = useMemo(() => {
    if (driverScheduleData) return getResources(driverScheduleData)
  }, [driverScheduleData])

  const events = useMemo(() => {
    if (driverScheduleData) return getEventsFromScheduleData(driverScheduleData, schedulePayload.shift)
  }, [driverScheduleData])

  const updateSchedules = (newSchedules) => {
    queryClient.setQueryData(['getDriverScheduleQuery', schedulePayload], () => newSchedules)
    cellCache.clearAll()
  }

  const setUpLinkSchedules = (schedule) => {
    const nextDate =
      schedulePayload.shift === shifts.PM
        ? moment(schedule.shift_start).add(1, 'd').format(dateFormat.YEAR_MONTH_DATE)
        : moment(schedule.shift_start).format(dateFormat.YEAR_MONTH_DATE)

    setSchedulePayload({
      shift: schedulePayload.shift === shifts.PM ? shifts.AM : shifts.PM,
      viewDate: nextDate,
    })
    setSelectedDepots([schedule.home_location])
    cellCache.clearAll()
  }

  useEffect(() => {
    checkForStatusUpdates()
    cellCache.clearAll()
  }, [driverScheduleData])

  const resetFromLink = async () => {
    setSelectedDepots(convertStorageStringToArray(store.getItem('order-dispatch-trip-depots')))
    cellCache.clearAll()
  }
  const linkToDriver = (driverName, viewDate, shift) => {
    setSchedulePayload((payload) => ({ ...payload, shift, viewDate }))
    setSelectedDrivers([driverName])
    cellCache.clearAll()
  }
  function getResources(data) {
    return data.map((schedule) => {
      return {
        title: schedule.driver_name,
        id: schedule.id,
        children: [
          {
            id: `Estimate-${schedule.id}`,
            title: 'Estimate',
            eventColor: '#76abf3',
          },
          {
            id: `Actual-${schedule.id}`,
            title: 'Actual',
            eventColor: '#64D28D',
          },
        ],
      }
    })
  }

  useEffect(() => {
    async function hotswapSchedules() {
      if (quickStatusResponse?.length > 0 && driverScheduleData?.length > 0) {
        // Using a map for quick lookups
        const quickStatusRespMap = new Map(quickStatusResponse.map((d) => [d.id, d]))

        const updatedSchedules = driverScheduleData.map((schedule) => {
          const match = quickStatusRespMap.get(schedule.id)

          if (match) {
            return structuredClone(match)
          }

          return schedule
        })

        updateSchedules(updatedSchedules)
      }
    }
    hotswapSchedules()
  }, [quickStatusResponse])

  const handleUnlink = async (order_id) => {
    const unlinkData = await unlinkOrderMutation.mutateAsync(order_id)
    updateSpecificDrivers(unlinkData?.driver_schedules)
  }

  const checkForStatusUpdates = async () => {
    setQuickPayload({ shift: schedulePayload.shift, date: schedulePayload.viewDate })
  }

  const addActionBlock = async (request) => {
    try {
      const { data } = await api.assignActionBlock(request)
      updateSpecificDrivers(data?.driver_schedules)
      cellCache.clearAll()
    } catch (e) {
      return false
    }
  }
  const removeActionBlock = async (request) => {
    try {
      const { data } = await api.removeActionBlock(request)
      updateSpecificDrivers(data?.driver_schedules)
      cellCache.clearAll()
    } catch (e) {
      return false
    }
  }
  const insertOrderBeforeActionBlock = async (request) => {
    try {
      const { data } = await api.insertBeforeActionBlock(request)
      updateSpecificDrivers(data?.driver_schedules)
    } catch (e) {
      return false
    }
  }

  const insertAssignedBeforeActionBlock = async (request) => {
    try {
      const { data } = await api.insertAssignedBeforeActionBlock(request)
      updateSpecificDrivers(data?.driver_schedules)
    } catch (e) {
      return false
    }
  }

  const addOrderToTrip = async (request, onComplete = null, flags) => {
    setisApplyingChange(true)

    try {
      const data = await assignOrderMutation.mutateAsync(request)

      setisApplyingChange(false)
      if (
        !handleOrderFailure(data, 'add', onComplete, () => addOrderToTrip({ ...request, confirmed: true }, onComplete))
      )
        return false

      setTimeout(() => optimisticallyRemoveOrder(request.order_id, queryClient))

      if (flags && flags.some((f) => f.type === 'dependent' && f.severity !== 'money')) {
        notification.open({
          message: `Dependent Order Added to Schedule`,
          description: 'We are currently updating the dependency tree. This will take just a second',
        })
      }

      return true
    } catch (error) {
      console.error(error)
      return false
    } finally {
      setisApplyingChange(false)
      onComplete && onComplete()
    }
  }

  const moveOrder = async (request, onComplete = null, action = dropActions.MOVE) => {
    setisApplyingChange(true)

    try {
      const data = await moveOrderMutation.mutateAsync(request)
      setisApplyingChange(false)
      if (
        !handleOrderFailure(data, action, onComplete, () =>
          moveOrder({ ...request, confirmed: true }, onComplete, action)
        )
      )
        return
      updateSpecificDrivers(data?.driver_schedules)
    } catch (error) {
      console.error(error)
    } finally {
      setisApplyingChange(false)
      onComplete && onComplete()
    }
  }

  const removeOrderFromTrip = async (request) => {
    setisApplyingChange(true)
    setLoadingTripCard({ driver_schedule_id: request.driver_schedule_id, slot_index: request.slot_index })
    try {
      const data = await removeOrderMutation.mutateAsync(request)
      if (!handleOrderFailure(data, 'Remove', undefined, () => removeOrderFromTrip({ ...request, confirmed: true })))
        return
      setTimeout(() => updateSpecificDrivers(data?.driver_schedules))
      setisApplyingChange(false)

      return data
    } catch (error) {
      console.error(error)
    } finally {
      setisApplyingChange(false)
      setLoadingTripCard(null)
    }
  }

  const handleOrderFailure = (data, type, onComplete = null, retry) => {
    if (data.status === 'failure') {
      notification.open({
        message: `${titleCase(type)} Order Failed`,
        description: data.message,
      })

      if (data.driver_schedules.length > 0) updateSpecificDrivers(data.driver_schedules)
      onComplete && onComplete()

      return false
    }

    if (data.status === 'confirm') {
      confirm({
        title: 'The order you dropped is outside of the loading window.',
        icon: <ExclamationCircleOutlined />,
        content: 'Are you sure you want to proceed?',
        onOk() {
          retry()
        },
        onCancel() {
          onComplete && onComplete()
        },
      })
      return false
    }
    return true
  }

  const updateSpecificDrivers = (newSchedules) => {
    queryClient.setQueryData(['getDriverScheduleQuery', schedulePayload], (cache) => {
      return cache.map((cachedSchedule) => {
        const matchIndex = newSchedules.findIndex((s) => s.id === cachedSchedule.id)
        if (matchIndex > -1) {
          return { ..._.cloneDeep(newSchedules[matchIndex]) }
        }
        return cachedSchedule
      })
    })
    cellCache.clearAll()
  }

  const handleDepotChange = (values) => {
    store.setItem('order-dispatch-trip-depots', values)
    const strings =
      values && values?.length > 0 && typeof values !== 'string' ? values.join(',') : values?.length === 0 ? '' : values
    updateSetting('order-dispatch-trip-depots', strings)
    setSelectedDepots(values)
    cellCache.clearAll()
  }

  const handleDriverChange = (values) => {
    if (values.length === 0) {
      store.removeItem('trip-grid-driver-name-filter')
      updateSetting('trip-grid-driver-name-filter', '')
    } else {
      store.setItem('trip-grid-driver-name-filter', values)
      const strings = values && values?.length > 0 && typeof values !== 'string' ? values.join(',') : values
      updateSetting('trip-grid-driver-name-filter', strings)
    }
    setSelectedDrivers(values)
    cellCache.clearAll()
  }

  const toggleShift = (selectedShift) => {
    if (selectedShift !== schedulePayload.shift) {
      setSchedulePayload((payload) => ({ ...payload, shift: selectedShift }))
      store.setItem('trip-grid-shift', selectedShift)
      updateSetting('trip-grid-shift', selectedShift)
    }
  }

  const handleEquipmentChange = async (values, scheduleId) => {
    await changeEquipmentMutation.mutateAsync({
      driver_schedule_id: scheduleId,
      tractor_number: values.tractor_number,
      trailer_number: values.trailer_number,
    })

    const updatedDriver = driverScheduleData.find((d) => d.id === scheduleId)
    if (values.tractor_number) {
      updatedDriver.current_tractor_number = values.tractor_number
    }
    if (values.trailer_number) {
      updatedDriver.current_trailer_number = values.trailer_number
      updatedDriver.current_trailer_config = values.trailer_type
    }
    updateSpecificDrivers([updatedDriver])
    setSelectedDrivers([...selectedDrivers]) //hack to get this to rerender
  }

  const unlockTrip = async (scheduleId, tripIndex, onComplete) => {
    return unlockTripMutation
      .mutateAsync({ driver_schedule_id: scheduleId, slot: tripIndex + 1 })
      .then((data) => {
        updateSpecificDrivers(data?.driver_schedules)
      })
      .finally(() => {
        onComplete && onComplete()
      })
  }

  const lockTrip = async (scheduleId, tripIndex, onComplete) => {
    const data = await lockTripMutation.mutateAsync({ driver_schedule_id: scheduleId, slot: tripIndex + 1 })
    updateSpecificDrivers(data?.driver_schedules)
    onComplete && onComplete()
  }

  return (
    <ScheduleContext.Provider
      value={{
        selectedDepots,
        setSelectedDepots,
        selectedDrivers,
        selectedOrders,
        setSelectedOrders,
        updateTripGrid: updateSpecificDrivers,
        schedules: driverScheduleData,
        calendarResources,
        dispatchViewMode,
        unfilteredResources,
        events,
        loadingSchedule,
        checkForStatusUpdates,
        removeOrderFromTrip,
        addOrderToTrip,
        moveOrder,
        handleDepotChange,
        handleDriverChange,
        toggleShift,
        refetchDriverSchedule,
        schedulePayload,
        setSchedulePayload,
        handleEquipmentChange,
        unlockTrip,
        lockTrip,
        setDispatchViewMode,
        setUpLinkSchedules,
        resetFromLink,
        linkToDriver,
        handleUnlink,
        addActionBlock,
        removeActionBlock,
        insertOrderBeforeActionBlock,
        insertAssignedBeforeActionBlock,
        cellCache,
        loadingTripCard,
        setLoadingTripCard,
      }}
    >
      {children}
    </ScheduleContext.Provider>
  )
}

export const useScheduleContext = () => {
  const context = useContext(ScheduleContext)
  if (context === undefined) {
    throw new Error('Context must be used within a Provider')
  }
  return context
}
